import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Facebook, Instagram, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";

// Instagram smart link — opens app on mobile, browser on desktop
const INSTAGRAM_URL = 'https://www.instagram.com/espace_hilanta/';
const openInstagram = (e) => {
  e.preventDefault();
  // Try to open the Instagram app first on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    window.location = 'instagram://user?username=espace_hilanta';
    // Fallback to browser after 1.5s if app not installed
    setTimeout(() => {
      window.open(INSTAGRAM_URL, '_blank');
    }, 1500);
  } else {
    window.open(INSTAGRAM_URL, '_blank');
  }
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { itemCount: cartItemCount } = useCart();
  const location = useLocation();

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Prestations', path: '/services' },
    { name: 'Réservation', path: '/reservation' },
    { name: 'Abonnements', path: '/abonnements' },
    { name: 'Offrir un Soin', path: '/carte-cadeau' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white/95 sticky top-0 z-50 shadow-sm" data-testid="navbar">
      {/* Top bar with social media */}
      <div className="bg-warm-brown text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:0960421822" className="flex items-center gap-1 hover:text-gold transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">09 60 42 18 22</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-white/80">Suivez-nous :</span>
            <a 
                href="https://www.facebook.com/people/Hilanta-Bien-%C3%AAtre/pfbid034xjX8MidrV5xgfZ27Xjorzux3HLxbKxrawbpPuBDqZ4BcBkrZ9uvVMh4QaFssXYwl/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-gold transition-colors"
                data-testid="nav-social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href={INSTAGRAM_URL}
                onClick={openInstagram}
                className="hover:text-gold transition-colors"
                data-testid="nav-social-instagram"
              >
              <Instagram size={18} />
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Hilanta" 
              className="h-14 w-14 rounded-full object-cover shadow-md border-2 border-gold/30"
            />
            <span className="font-heading text-2xl font-semibold text-warm-brown hidden sm:block">
              HILANTA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`font-body text-base transition-colors duration-200 relative ${
                  isActive(link.path)
                    ? 'text-terracotta font-semibold'
                    : 'text-warm-brown hover:text-terracotta'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-terracotta rounded-full"></span>
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button + Cart */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/carte-cadeau/panier" className="relative" data-testid="navbar-cart-link">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-terracotta/10 text-warm-brown relative"
                aria-label="Panier cartes cadeaux"
              >
                <ShoppingBag size={20} />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-terracotta text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full font-bold px-1" data-testid="navbar-cart-badge">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/reservation">
              <Button 
                className="bg-gradient-to-r from-terracotta to-terracotta-600 hover:from-terracotta-600 hover:to-terracotta text-white rounded-full px-6 py-2 font-body shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="nav-cta-button"
              >
                Réserver maintenant
              </Button>
            </Link>
          </div>

          {/* Mobile right side: cart + menu button */}
          <div className="flex lg:hidden items-center gap-1">
            <Link to="/carte-cadeau/panier" className="relative p-2" data-testid="navbar-cart-link-mobile" aria-label="Panier cartes cadeaux">
              <ShoppingBag size={22} className="text-warm-brown" />
              {cartItemCount > 0 && (
                <span className="absolute top-0 right-0 bg-terracotta text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full font-bold px-1" data-testid="navbar-cart-badge-mobile">
                  {cartItemCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-warm-brown"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-sand" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`font-body text-lg py-2 ${
                    isActive(link.path)
                      ? 'text-terracotta font-semibold'
                      : 'text-warm-brown'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex items-center gap-4 py-2">
                <a 
                  href="https://www.facebook.com/people/Hilanta-Bien-%C3%AAtre/pfbid034xjX8MidrV5xgfZ27Xjorzux3HLxbKxrawbpPuBDqZ4BcBkrZ9uvVMh4QaFssXYwl/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-warm-brown rounded-full flex items-center justify-center text-white"
                >
                  <Facebook size={20} />
                </a>
                <a 
                  href={INSTAGRAM_URL}
                  onClick={openInstagram}
                  className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white"
                >
                  <Instagram size={20} />
                </a>
              </div>
              <Link to="/reservation" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-full">
                  Réserver maintenant
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
